import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getNewsByFilter } from "../../api/apiHandler";
import NewsCard from "../../components/NewsCard";
import BreakingNews from "../../components/BreakingNews";
import TopAdBanner from "../../components/TopAdBanner";
import LeftAdBanner from "../../components/LeftAdBanner";
import WeatherWidget from "../../components/WeatherWidget";
import logo from "../../Assets/images/mlogo.png";
import { getMediaBaseUrl } from "../../utils/backendUrl";

const BASE_URL = getMediaBaseUrl();

const resolveImage = (item, field) => {
  const value = item?.[field];
  if (!value) return null;
  if (value.startsWith("http")) return value;
  if (value.startsWith("/images/")) return value;
  return `${BASE_URL}/uploads/news/${encodeURIComponent(value)}`;
};

const SearchPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [input, setInput] = useState(query);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    let isMounted = true;
    setLoading(true);
    setError("");
    getNewsByFilter({ q: query, limit: 50 })
      .then((res) => {
        if (isMounted) setResults(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (isMounted) setError(err.message || "खोज में त्रुटि हुई");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [query]);

  const submitSearch = (value) => {
    const term = (value || input).trim();
    if (term) {
      setSearchParams({ q: term });
    }
  };

  const handleVoiceSearch = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("आपका ब्राउज़र वॉइस सर्च का समर्थन नहीं करता है।");
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SR();
    recognitionRef.current = recognition;
    recognition.lang = i18n.language === "en" ? "en-IN" : "hi-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setSearchParams({ q: transcript });
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        alert("माइक्रोफ़ोन की अनुमति नहीं दी गई। ब्राउज़र सेटिंग में अनुमति दें।");
      } else if (event.error === "no-speech") {
        alert("कुछ सुनाई नहीं दिया। कृपया पुनः प्रयास करें।");
      }
    };

    recognition.onend = () => setIsListening(false);

    recognition.start();
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      <div className="bg-gradient-to-br from-[#2d006b] to-[#4600a3] text-white py-6 md:py-8 mb-6 relative overflow-hidden shadow-inner">
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMCwwLDAsMC4wNSkiLz48L3N2Zz4=')]" />
        <div className="max-w-7xl mx-auto px-4 relative z-10 flex flex-col items-center text-center">
          <span className="text-purple-200 text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-1">खोज</span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-black capitalize tracking-tight mb-4 drop-shadow-md">
            {query ? `"${query}"` : "खोजें"}
          </h1>

          <div className="w-full max-w-2xl flex items-center bg-white rounded-full overflow-hidden shadow-lg">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
              placeholder={t('sidebar.search')}
              className="w-full px-5 py-3 outline-none text-sm text-gray-700 font-medium"
            />
            <button
              onClick={handleVoiceSearch}
              className={`px-4 py-3 transition-colors flex items-center justify-center border-l border-gray-200 ${isListening ? "text-purple-600 bg-purple-50 animate-pulse" : "text-gray-500 hover:text-brand-red hover:bg-gray-50"}`}
              title="वॉइस सर्च"
            >
              <i className={`fa-solid ${isListening ? "fa-microphone-lines" : "fa-microphone"}`}></i>
            </button>
            <button
              onClick={() => submitSearch()}
              className="bg-purple-700 hover:text-brand-red text-white px-6 py-3 transition-colors flex items-center justify-center"
            >
              <i className="fa-solid fa-magnifying-glass"></i>
            </button>
          </div>
        </div>
      </div>

      <TopAdBanner />

      <div className="max-w-[1400px] mx-auto px-4">
        <BreakingNews />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
          <div className="hidden lg:block lg:col-span-2">
            <div className="space-y-3 sticky top-[70px]">
              <LeftAdBanner />
              <WeatherWidget defaultCity="खजुराहो" />
            </div>
          </div>

          <div className="lg:col-span-8">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent"></div>
              </div>
            ) : error ? (
              <div className="text-center py-20 text-gray-500 text-sm">{error}</div>
            ) : query && results.length === 0 ? (
              <div className="text-center py-20 text-gray-500 text-sm">
                <i className="fa-solid fa-magnifying-glass text-4xl text-gray-300 mb-3 block"></i>
                कोई परिणाम नहीं मिला
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-20 text-gray-400 text-sm">
                कुछ खोजने के लिए ऊपर सर्च करें
              </div>
            ) : (
              <>
                <div className="mb-4 text-sm text-gray-600 font-semibold">
                  {results.length} परिणाम मिले
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {results.map((item, idx) => (
                    <NewsCard
                      key={item.id || idx}
                      image={resolveImage(item, "thumbnail") || logo}
                      category={item.category}
                      state={item.state}
                      title={item.news_title || item.title}
                      author={item.reporter_name || item.author}
                      reporter_image={resolveImage(item, "reporter_image")}
                      date={item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}
                      readTime="2 Min"
                      fullNews={item.description || item.fullNews}
                      videoUrl={item.youtube_url}
                      id={item.id || item._id}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;